import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Lock,
  Key,
  Fingerprint,
  AlertTriangle,
  Eye,
  EyeOff,
  RefreshCw,
  Copy,
  Check,
} from 'lucide-react'
import { useAppStore } from '../store'
import { api } from '../lib/api'
import {
  generateVaultId,
  randomBytes,
  toBase64,
  deriveKeyFromPassphrase,
  deriveVerificationTag,
} from '../lib/crypto'
import {
  isPlatformAuthenticatorAvailable,
  createPasskeyCredential,
} from '../lib/passkey'
import { CONFIG, createEmptyVaultData } from '../config'
import { KeyCard, KeyCardStack } from '../components/vault/KeyCard'
import { groupVaultId } from '../lib/folderColors'

type AuthMethod = 'passphrase' | 'passkey'

export function CreateVaultPage() {
  const navigate = useNavigate()
  const userEmail = useAppStore((s) => s.userEmail)
  const addBookmarkedVault = useAppStore((s) => s.addBookmarkedVault)
  const setUnlockedVault = useAppStore((s) => s.setUnlockedVault)

  const [vaultId, setVaultId] = useState(() => generateVaultId())
  const [authMethod, setAuthMethod] = useState<AuthMethod>('passphrase')
  const [passphrase, setPassphrase] = useState('')
  const [confirmPassphrase, setConfirmPassphrase] = useState('')
  const [showPassphrase, setShowPassphrase] = useState(false)
  const [passkeySupported, setPasskeySupported] = useState<boolean | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Check passkey support on mount
  useEffect(() => {
    isPlatformAuthenticatorAvailable().then(setPasskeySupported)
  }, [])

  const regenerateVaultId = () => {
    setVaultId(generateVaultId())
  }

  const copyVaultId = async () => {
    await navigator.clipboard.writeText(vaultId)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const passphraseStrength = (): {
    label: string
    level: number
    color: string
  } => {
    if (!passphrase) return { label: '', level: 0, color: '' }
    if (passphrase.length < 8)
      return { label: 'Too short', level: 1, color: '#FF8FA3' }
    if (passphrase.length < 12)
      return { label: 'Weak', level: 2, color: '#F0956A' }
    if (passphrase.length < 16)
      return { label: 'Good', level: 3, color: '#F2CC6B' }
    return { label: 'Strong', level: 4, color: '#7FE0B0' }
  }

  const canSubmit =
    authMethod === 'passkey' ||
    (passphrase.length >= 8 && passphrase === confirmPassphrase)

  const handleCreateVault = async () => {
    if (!canSubmit || isCreating || !userEmail) return

    setIsCreating(true)
    setError(null)

    try {
      const salt = randomBytes(CONFIG.SALT_LENGTH)
      let key: Uint8Array
      let credentialId: string | undefined
      let publicKey: string | undefined

      if (authMethod === 'passkey') {
        // Use the stored vault salt as the PRF input so unlock (which
        // evaluates PRF with meta.salt) reproduces the same output.
        const result = await createPasskeyCredential(vaultId, userEmail, salt)

        if (!result) {
          setError(
            'Failed to create passkey. Your browser may not support the PRF extension.',
          )
          setIsCreating(false)
          return
        }

        credentialId = result.credentialId
        publicKey = result.publicKey
        key = await deriveKeyFromPassphrase(toBase64(result.prfOutput), salt)
      } else {
        key = await deriveKeyFromPassphrase(passphrase, salt)
      }

      const verificationTag = await deriveVerificationTag(key, salt)

      // Create vault on server
      await api.createVault({
        id: vaultId,
        authType: authMethod,
        salt: toBase64(salt),
        verificationTag: toBase64(verificationTag),
        credentialId,
        publicKey,
      })

      // Add to bookmarks
      await api.addBookmark(vaultId)
      addBookmarkedVault(vaultId)

      // Set as unlocked vault with empty data
      setUnlockedVault({
        id: vaultId,
        meta: {
          authType: authMethod,
          salt: toBase64(salt),
          verificationTag: toBase64(verificationTag),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          size: 0,
          credentialId,
          publicKey,
        },
        data: createEmptyVaultData(),
        key,
        verificationTag,
        isDirty: false,
      })

      // Navigate to vault
      navigate(`/v/${vaultId}`)
    } catch (err) {
      console.error('Failed to create vault:', err)
      setError(err instanceof Error ? err.message : 'Failed to create vault')
    } finally {
      setIsCreating(false)
    }
  }

  const strength = passphraseStrength()
  const methodIndex = authMethod === 'passphrase' ? 0 : 1
  const mismatch = !!confirmPassphrase && passphrase !== confirmPassphrase

  return (
    <div className="mx-auto grid max-w-[1040px] gap-8 lg:grid-cols-[400px_minmax(0,1fr)] lg:gap-12">
      {/* Live preview */}
      <div className="lg:sticky lg:top-[100px] lg:self-start">
        <div className="mb-6 animate-fade-up">
          <h1 className="font-display text-[30px] font-semibold leading-tight tracking-tight sm:text-[38px]">
            New vault
          </h1>
          <p className="mt-1 text-muted">
            An encrypted space for links and notes.
          </p>
        </div>
        <KeyCardStack spread={isCreating}>
          <KeyCard
            vaultId={groupVaultId(vaultId)}
            corner={
              authMethod === 'passkey' ? (
                <Fingerprint
                  className="h-5 w-5 text-white/70"
                  aria-label="Passkey"
                />
              ) : (
                <Key
                  className="h-5 w-5 text-white/70"
                  aria-label="Passphrase"
                />
              )
            }
            footerLeft={
              authMethod === 'passkey'
                ? 'Unlocks with a passkey'
                : 'Unlocks with a passphrase'
            }
            footerRight={
              <span className="inline-flex min-h-[28px] items-center rounded-full bg-white/10 px-3 text-[12.5px] font-bold">
                {isCreating ? 'Creating' : 'Draft'}
              </span>
            }
          />
        </KeyCardStack>
      </div>

      {/* Form */}
      <div className="space-y-4">
        <section
          className="panel p-5 sm:p-6 animate-fade-up"
          style={{ animationDelay: '100ms' }}
        >
          <span className="label">Vault ID</span>
          <div className="flex gap-2">
            <div className="input flex flex-1 items-center font-mono text-[17px] tracking-[0.12em]">
              {vaultId}
            </div>
            <button
              onClick={copyVaultId}
              className="icon-btn glass !h-[50px] !w-[50px]"
              aria-label="Copy vault ID"
            >
              {copied ? (
                <Check className="h-5 w-5 text-[#7FE0B0]" />
              ) : (
                <Copy className="h-5 w-5" />
              )}
            </button>
            <button
              onClick={regenerateVaultId}
              className="icon-btn glass !h-[50px] !w-[50px]"
              aria-label="Generate a new ID"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
          <p className="mt-2 text-[13px] text-faint">
            Share this ID with people who should be able to open the vault.
          </p>
        </section>

        <section
          className="panel p-5 sm:p-6 animate-fade-up"
          style={{ animationDelay: '160ms' }}
        >
          <span className="label">How it unlocks</span>
          <div
            className="seg glass flex w-full"
            role="group"
            aria-label="Authentication method"
            style={{ '--i': methodIndex, '--n': 2 } as React.CSSProperties}
          >
            <span className="seg-pill" aria-hidden="true" />
            <button
              type="button"
              className="seg-btn flex min-h-[48px] items-center justify-center gap-2"
              aria-pressed={authMethod === 'passphrase'}
              onClick={() => setAuthMethod('passphrase')}
            >
              <Key className="h-4 w-4" />
              Passphrase
            </button>
            <button
              type="button"
              className="seg-btn flex min-h-[48px] items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-40"
              aria-pressed={authMethod === 'passkey'}
              disabled={!passkeySupported}
              onClick={() => passkeySupported && setAuthMethod('passkey')}
            >
              <Fingerprint className="h-4 w-4" />
              Passkey
            </button>
          </div>
          <p className="mt-2 text-[13px] text-faint">
            {authMethod === 'passkey'
              ? 'Use Face ID, Touch ID, Windows Hello or a security key.'
              : passkeySupported === false
                ? 'Passkeys aren’t supported on this device, so this vault will use a passphrase.'
                : 'Pick something long and memorable — a short sentence works well.'}
          </p>

          {authMethod === 'passphrase' && (
            <div className="mt-5 space-y-4 animate-fade-up">
              <div>
                <label className="label" htmlFor="new-pass">
                  Passphrase
                </label>
                <div className="input flex items-center gap-2 !pr-1.5">
                  <input
                    id="new-pass"
                    type={showPassphrase ? 'text' : 'password'}
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    autoComplete="new-password"
                    className="min-h-[48px] min-w-0 flex-1 bg-transparent outline-none placeholder:text-faint"
                    placeholder="At least 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassphrase(!showPassphrase)}
                    className="icon-btn !h-10 !w-10"
                    aria-label={
                      showPassphrase ? 'Hide passphrase' : 'Show passphrase'
                    }
                  >
                    {showPassphrase ? (
                      <EyeOff className="h-[18px] w-[18px]" />
                    ) : (
                      <Eye className="h-[18px] w-[18px]" />
                    )}
                  </button>
                </div>
                <div
                  className="mt-2.5 flex items-center gap-3"
                  aria-live="polite"
                >
                  <div
                    className="grid flex-1 grid-cols-4 gap-1.5"
                    aria-hidden="true"
                  >
                    {[1, 2, 3, 4].map((n) => (
                      <span
                        key={n}
                        className="h-1.5 rounded-full transition-colors duration-300"
                        style={{
                          background:
                            strength.level >= n
                              ? strength.color
                              : 'rgba(255,255,255,0.1)',
                        }}
                      />
                    ))}
                  </div>
                  <span
                    className="w-20 text-right text-[13px] font-semibold"
                    style={{ color: strength.color || 'var(--faint)' }}
                  >
                    {strength.label || 'Strength'}
                  </span>
                </div>
              </div>
              <div>
                <label className="label" htmlFor="confirm-pass">
                  Confirm passphrase
                </label>
                <input
                  id="confirm-pass"
                  type={showPassphrase ? 'text' : 'password'}
                  value={confirmPassphrase}
                  onChange={(e) => setConfirmPassphrase(e.target.value)}
                  autoComplete="new-password"
                  className={`input ${mismatch ? 'is-err' : ''}`}
                  placeholder="Type it again"
                />
                {mismatch && (
                  <p className="mt-2 text-[13px] text-[#FFA3B4]">
                    Passphrases don’t match.
                  </p>
                )}
              </div>
            </div>
          )}
        </section>

        <div
          className="flex gap-3 rounded-[22px] border border-[rgba(242,204,107,0.25)] bg-[rgba(242,204,107,0.07)] p-4 animate-fade-up"
          style={{ animationDelay: '220ms' }}
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-[#F2CC6B]" />
          <div>
            <p className="text-sm font-bold text-[#F7DE9C]">
              There’s no recovery
            </p>
            <p className="mt-0.5 text-[13.5px] text-[#E4D6B0]">
              If you forget the passphrase or lose the passkey, nobody —
              including us — can decrypt this vault. Store it somewhere safe.
            </p>
          </div>
        </div>

        {error && (
          <p
            role="alert"
            className="rounded-2xl border border-[rgba(255,120,150,0.25)] bg-[rgba(255,120,150,0.08)] p-4 text-sm text-[#FFB8C5]"
          >
            {error}
          </p>
        )}

        <button
          onClick={handleCreateVault}
          disabled={!canSubmit || isCreating}
          className="btn-primary min-h-[56px] w-full rounded-[18px] text-base animate-fade-up"
          style={{ animationDelay: '260ms' }}
        >
          {isCreating ? (
            <RefreshCw className="h-5 w-5 animate-spin" />
          ) : (
            <Lock className="h-5 w-5" />
          )}
          {isCreating ? 'Creating vault…' : 'Create vault'}
        </button>
      </div>
    </div>
  )
}
